import React, { useState, useEffect } from 'react';
import { Helmet } from "react-helmet";
import { useParams, useLocation, useNavigate } from 'react-router-dom';

import BreadCrumbs from '../BreadCrumbs';
import ConsensusSummary from './ConsensusSummary';
import ReviewStatus from '../../config/ReviewStatus.js';
import SessionConstants from '../../config/SessionConstants';
import Format from '../../utils/Format';
import APIUtils from '../../utils/APIUtils';
import HGVSNames from '../HGVSNames';
import VariantInfoGrid from '../VariantInfoGrid';
import Loading from '../Loading';
import '../../index.css';

const VariantConsensus = () => {
  const { patientId, variantId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [breadCrumbs, setBreadCrumbs] = useState([]);
  const [programId, setProgramId] = useState(null);
  const [reviewStatus, setReviewStatus] = useState(null);
  const [summary, setSummary] = useState({});
  const [variantType, setVariantType] = useState('');
  const [annotationLastUpdated, setAnnotationLastUpdated] = useState('');
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [hgvsLoaded, setHgvsLoaded] = useState(false);
  const [consensusLoaded, setConsensusLoaded] = useState(false);

  useEffect(() => {
    // Get breadCrumbs from location.state, add current page
    let crumbs = location.state?.breadCrumbs || [];
    crumbs = [...crumbs, { name: 'VariantConsensus' }];
    setBreadCrumbs(crumbs);

    setProgramId(sessionStorage.getItem(SessionConstants.PROGRAM_ID));
    setReviewStatus(location.state?.reviewStatus);

    // Set summary info from location.state
    setSummary({
      variantName: location.state?.variantName || '',
      gene: location.state?.gene || '',
      potentialCondition: location.state?.potentialCondition || ''
    });

    // Fetch the review to source annotation info (variant type / last updated),
    // matching how VariantDetails populates the VariantInfoGrid.
    (async () => {
      try {
        const response = await APIUtils.getReview(
          sessionStorage.getItem(SessionConstants.PROGRAM_ID),
          variantId
        );
        setVariantType(response?.annotationInfo?.variantType || '');
        setAnnotationLastUpdated(response?.annotationInfo?.lastUpdatedDate || '');
      } catch (err) {
        console.error('Error fetching review annotation info:', err);
      }
    })();
  }, [patientId, variantId, location.state]);

  // Keep the page content hidden until both the HGVS names and the consensus
  // data have loaded, so everything appears at once instead of popping in.
  useEffect(() => {
    setHgvsLoaded(false);
    setConsensusLoaded(false);
  }, [variantId]);

  const openModal = () => setModalIsOpen(true);

  const variantName = summary?.variantName || '';
  const gene = summary?.gene;
  const condition = summary?.potentialCondition;
  const allLoaded = hgvsLoaded && consensusLoaded;

  return (
    <div className="variantDetails">
      <Helmet>
        <title>ED3N - Variant Consensus</title>
      </Helmet>
      <BreadCrumbs breadCrumbs={breadCrumbs} />
      <h1 className="pageTitle">
        Variant Name: {variantName}
        {reviewStatus === ReviewStatus.CLOSED ? (
          <span className="pageSubTitle">
            <strong>Published</strong>
          </span>
        ) : null}
      </h1>
      <section>
        <div className="section">
          {!allLoaded && <Loading isLoading={true} />}
          <div style={{ display: allLoaded ? 'block' : 'none' }}>
            <section>
              <h2 className="sectionHeading">Summary</h2>
              <VariantInfoGrid 
                // variantName={variantName}
                gene={gene}
                condition={condition}
                hgvsNames={<HGVSNames variantId={variantId} onLoaded={() => setHgvsLoaded(true)} />}
                variantType={variantType}
                annotationLastUpdated={annotationLastUpdated}
                onViewAnnotationData={() =>
                  navigate('/variantannotationdata', {
                    state: {
                      variantObjectId: variantId,
                      variantName: variantName,
                      breadCrumbs: breadCrumbs,
                    },
                  })
                }
              />
            </section>
            <ConsensusSummary
              programId={programId}
              variantId={variantId}
              reviewStatus={reviewStatus}
              onLoaded={() => setConsensusLoaded(true)}
            />
          </div>
        </div>
      </section>
    </div>
  );
};

export default VariantConsensus;